// ======================================
// Authorization and user interaction. Data
// ======================================
//
// SENTRY INTEGRATION - User Context
//
// Changes made for Sentry:
// - Import setSentryUser from sentry.js
// - Call setSentryUser() in updateUserData() when user logs in
//
// How it works in Sentry process:
// - When user logs in, their id/email/organization is attached to all future errors
// - This helps identify which user experienced the error
// - When user logs out, context is cleared so errors aren't linked to wrong user
// ======================================
import {setSentryUser, clearSentryUser} from "../sentry";

import {makeAutoObservable} from "mobx";
import i18next from "i18next";
//local
import {TOKEN, LOGOUT_TIMEOUT_MS} from "../constants/auth.const";
import {getStorage} from "../helpers/storage";
import {roles, joinRequestStatuses} from "constants/auth.const";
import axiosInstance from "../axios/index";
import {setStorage} from "../helpers/storage";
import {showSuccess, showError} from "../helpers/notifications.helper";
import {formatCompanyNumber} from "../helpers/helper";
import OrganizationsStore from "./organizations.store";

const authApiUrl = process.env.REACT_APP_USE_SMS_API === "yes" ? "auth" : "auth-dev";

// eslint-disable-next-line no-console
console.info("Send real SMS:", process.env.REACT_APP_USE_SMS_API);

export class AuthStore {
  // STATE. Observable part
  request_id = null;
  phoneNumber = null; // *** probably not used
  modalName = null; // name of the specific modal window
  loading = true; // for loading states on auth pages
  btnLoading = false; // for loading states on the buttons
  joinRequest = null; // array of invites from the user
  qrCodeData = null; // data for qrCode picture
  autoStartToken = null; // bankid: Used to compile the start URL.
  orderRef = null; // number of bankid verification
  signContractSideData = {}; // data with contract signatures on buyer/seller side
  bankIdVerifyData = {}; // data for bankid verification
  organizations = []; // user organization
  token = getStorage(TOKEN); // user token
  counter = 0;
  hintCode = null;
  logoutLoading = false; // for loading states on the logout buttons
  // user information
  user = {
    email: "",
    firstName: "",
    id: "",
    lastName: "",
    mobilePhone: "",
    language: "en",
    personalNumber: null,
    permissions: [],
    entraUserId: "",
  };
  subscribedPermissions = null; // permissions are adjusted in accordance with the availability in subscriptions

  // STATE. Computed part

  // current organization
  get activeOrganization() {
    return this.user.ActiveOrganization;
  }
  // name of the current organization
  get organizationName() {
    return this.activeOrganization?.name;
  }

  // id of the current organization
  get organizationId() {
    return this.activeOrganization?.id;
  }

  // subscription of the current organization
  get organizationSubscription() {
    return this.activeOrganization?.SubscribeUnit;
  }

  get activeOrganizationId() {
    return this.activeOrganization?.id;
  }

  get hasActiveOrganization() {
    return !!this.activeOrganization?.id;
  }

  get myOrganizations() {
    return this.user.Organizations;
  }

  // getting the authorization status
  get isAuthorized() {
    return !!this.token;
  }

  // getting the client role
  get clientRole() {
    return this.activeOrganization?.role;
  }

  // getting confirmation that the client is not a member of the organization
  get isStaff() {
    return this.clientRole !== roles.MEMBER;
  }

  // getting confirmation that the client is just a member of the organization
  get isMember() {
    return this.clientRole === roles.MEMBER;
  }

  get hasUserPermissions() {
    return !!this.user.permissions.length;
  }

  // current member
  get memberId() {
    return this.user.OrganizationMemberId;
  }

  get isEntraUser() {
    return !!this.user.entraUserId;
  }

  // signed up for a company that already has an account and is not let in yet
  get isApplicant() {
    return (
      !!this.user.id && !this.hasActiveOrganization && !!this.user.isApplyingToExistingOrg
    );
  }

  // the last answer to the applicant's request, when nothing is waiting
  get joinStatus() {
    return this.user.JoinStatus;
  }

  get isJoinDenied() {
    return (
      this.isApplicant &&
      !this.joinRequest?.length &&
      this.joinStatus?.status === joinRequestStatuses.DENIED
    );
  }

  // the request was withdrawn or never made: the applicant chooses the company again
  get needsCompany() {
    return this.isApplicant && !this.joinRequest?.length && !this.isJoinDenied;
  }

  constructor() {
    makeAutoObservable(this);
  }

  // ACTIONS
  // ****** REGISTRATION AND AUTHENTICATION ******

  // setting the request id in the store
  setRequestId(value) {
    this.request_id = value;
  }

  // writing the token to the local storage and to auth-part store
  setToken(value) {
    setStorage(TOKEN, value);
    this.token = value;
  }

  // ending the session on the server, then clearing the browser and going to the start page
  logout() {
    if (this.logoutLoading) return;
    this.logoutLoading = true;
    const endSession = this.token
      ? axiosInstance
          .post(
            "auth/logout",
            {},
            {suppressErrors: true, isLogout: true, timeout: LOGOUT_TIMEOUT_MS}
          )
          .catch(() => null)
      : Promise.resolve();
    return endSession.then(() => {
      clearSentryUser();
      localStorage.clear();
      this.token = null;
      window.location.replace("/");
    });
  }

  // setting the phone number in the store
  setPhoneNumber(value) {
    this.phoneNumber = value;
  }

  // setting the organizations in the store
  setOrganizations(organizations) {
    this.organizations = organizations;
  }

  // setting the subscribed permissions in the store
  setSubscribedPermissions(value) {
    this.subscribedPermissions = value;
  }

  // sending a phone number during registration
  sendNumber(number) {
    this.updateBtnLoading(true);
    if (!this.token) {
      return axiosInstance
        .post(`${authApiUrl}/send-verify-sms`, {number})
        .then(({data}) => {
          if (data?.isEntraUser && data?.redirectUrl && typeof window !== "undefined") {
            const restBase =
              axiosInstance?.defaults?.baseURL || process.env.REACT_APP_API_URL || "";
            const rootBase = restBase.replace(/\/v2\/?$/, "").replace(/\/$/, "");
            const target = data.redirectUrl.startsWith("http")
              ? data.redirectUrl
              : `${rootBase}${data.redirectUrl.startsWith("/") ? "" : "/"}${
                  data.redirectUrl
                }`;
            window.location.href = target;
            return data;
          }
          data.response?.request_id && this.setRequestId(data.response.request_id);
          return data;
        })
        .finally(() => this.updateBtnLoading());
    }
  }

  // sending a verification sms code
  checkCode(number, code, request_id) {
    this.updateBtnLoading(true);
    const body = {number, code, request_id};
    if (!this.token) {
      return axiosInstance
        .post(`${authApiUrl}/check-sms-code`, body, {suppressErrors: true})
        .then(({data}) => {
          this.setToken(data.token);
          return data;
        })
        .finally(() => this.updateBtnLoading());
    }
  }

  // checking at onboarding whether the email already belongs to a user
  checkOnboardingEmail(email) {
    return axiosInstance
      .post("auth/onboarding-check-email", {email}, {suppressErrors: true})
      .then(({data}) => data.isUsed);
  }

  // emailing a one-time login link when the SMS code didn't arrive; the answer is the same whether or not it was sent
  sendLoginLink(number) {
    return axiosInstance.post("auth/send-login-link", {number});
  }

  // logging in with a one-time login link from support or from the email; an earlier login in this browser is dropped
  loginWithLink(token) {
    return axiosInstance
      .post("auth/login-link", {token}, {suppressErrors: true})
      .then(({data}) => {
        localStorage.clear();
        this.setToken(data.token);
        return data;
      });
  }

  // sending the completed registration form for checking values
  sendFormForCheck(values) {
    this.updateBtnLoading(true);
    const body = {
      ...values,
      companyNumber: formatCompanyNumber(values.companyNumber, true),
    };
    return axiosInstance
      .post(`${authApiUrl}/onboarding-check-send-sms`, body, {suppressErrors: true})
      .then(({data}) => {
        this.setRequestId(data.response.request_id);
        return data;
      })
      .finally(() => this.updateBtnLoading());
  }

  // sending the completed registration form
  sendForm(values) {
    this.updateBtnLoading(true);
    const body = {
      ...values,
      companyNumber: formatCompanyNumber(values.companyNumber, true),
    };
    return axiosInstance
      .post(`${authApiUrl}/onboarding`, body, {suppressErrors: true})
      .then(({data}) => {
        this.setToken(data.token);
        if (data && data.company) {
          const {
            company: {Organizations, companyNumber},
          } = data;
          setStorage("companyNumber", companyNumber);
          this.setOrganizations(Organizations);
          return data;
        }
      })
      .finally(() => this.updateBtnLoading());
  }

  // checking the token
  checkToken(notLoading) {
    if (this.token) {
      this.loading = !notLoading;
      return axiosInstance
        .get(`user/get-user-data-by-token`)
        .then(({data: {user}}) => {
          this.updateUserData(user);
          this.joinRequest = user.JoinRequest;
          user.ActiveOrganization && OrganizationsStore.getOrganizationMembers();
          return user;
        })
        .catch((error) => {
          error.message === "Network Error" && showError(error.message);
        })
        .finally(() => (this.loading = false));
    } else {
      this.loading = false;
      return Promise.reject();
    }
  }

  // adding a note to the request sent at onboarding
  updateJoinRequestMessage(joinMessage) {
    return axiosInstance
      .put("organization/join-request/message", {joinMessage})
      .then(() => this.checkToken(true));
  }

  // withdrawing the request sent at onboarding to choose another company
  withdrawJoinRequest() {
    return axiosInstance
      .post("organization/join-request/withdraw")
      .then(() => this.checkToken(true));
  }

  // ****** WORKING WITH THE USER'S ACCOUNT ******

  // getting the current user's data
  getUserData() {
    if (!this.organizationId) return axiosInstance.get("user/get-user-data");
  }

  // sending user data for updating
  updateUser(values, noNeedMessage) {
    return axiosInstance.post("user/edit", values).then(({data}) => {
      !noNeedMessage && showSuccess(data?.message);
      return data?.user;
    });
  }

  // recording user data in the auth-part store
  updateUserData(data) {
    if (!this?.user) return;
    this.user = {
      ...this.user,
      ...data,
    };

    setSentryUser(this.user);
  }

  // changing the loading status for pages
  updateLoading(active) {
    this.loading = active;
  }

  // changing the loading status for buttons
  updateBtnLoading(active) {
    this.btnLoading = active;
  }

  // setting a name for a modal window (showing a specific model)
  updateAuthModalName(modalName) {
    this.modalName = modalName;
  }

  // setting the delay period between certain actions
  setCounter(value) {
    this.counter = value;
  }

  // sending a confirmation code to an email address
  async sendConfirmationEmail() {
    try {
      return await axiosInstance.post("user/send-confirmation-email");
    } catch (e) {
      throw new Error(e);
    }
  }

  // sending a verification email code for verification purposes
  async checkConfirmationCode(code) {
    try {
      return (await axiosInstance.post("user/check-confirmation-code", {code})).data;
    } catch (e) {
      throw new Error(e);
    }
  }

  // setting up an active organization for a user
  sendSetActiveOrganization(values) {
    return axiosInstance
      .post(`user/set-active-organization`, {
        ...values,
      })
      .then(() => undefined)
      .finally(() => {
        this.checkToken();
      });
  }

  // ****** WORKING WITH BANKID ******

  // getting BankID data for signing
  getBankIdData() {
    axiosInstance.post("auth/bankid").then(
      ({data}) => {
        this.setBankIdData(data);
        this.getBankIdQrCode();
      },
      () => {
        this.updateAuthModalName(null);
      }
    );
  }

  getBankIdQrCode() {
    axiosInstance.post("auth/bankid/qrcode-data", {orderRef: this.orderRef}).then(
      ({data}) => {
        this.setBankIdData(data);
        this.setBankIdVerifyData(data);
        if (data.completionData) this.getBankIdVerify(data.completionData);
      },
      () => {
        this.updateAuthModalName(null);
      }
    );
  }

  cancelAuthBankId() {
    axiosInstance
      .post("auth/bankid/cancel", {orderRef: this.orderRef})
      .catch(() => this.updateAuthModalName(null));
  }

  // getting data confirming BankId verification
  getBankIdVerify(completionData) {
    return axiosInstance.post("auth/bankid/verify", {completionData}).then(({data}) => {
      this.setBankIdVerifyData(data);
    });
  }

  // recording the data received after BankID in the store on the buyer/seller side
  setSignContractSideData(contractId, side, userId) {
    this.signContractSideData = {
      contractId,
      side,
      userId,
    };
  }

  // getting data confirming BankId verification on Contract signing
  getBankIdDataOnContract() {
    axiosInstance
      .post(
        `rfp/${this.signContractSideData.side}/contract/${this.signContractSideData.contractId}/signatory/${this.signContractSideData.userId}/sign`
      )
      .then(({data}) => {
        this.setBankIdData({
          orderRef: data.orderRef,
          qrCodeData: null,
          autoStartToken: data.autoStartToken,
        });
      });
  }

  getBankIdQRCodeOnContract() {
    axiosInstance
      .post(
        `rfp/${this.signContractSideData.side}/contract/${this.signContractSideData.contractId}/signatory/${this.signContractSideData.userId}/qr-code`,
        {
          orderRef: this.orderRef,
        }
      )
      .then(({data}) => {
        this.setBankIdData(data);
        this.setBankIdVerifyData(data);
        if (data.completionData) this.getBankIdVerifyOnContract(data.completionData);
      });
  }

  // getting data confirming BankId verification on Contract signing
  getBankIdVerifyOnContract(completionData) {
    return axiosInstance
      .post(
        `rfp/${this.signContractSideData.side}/contract/${this.signContractSideData.contractId}/signatory/${this.signContractSideData.userId}/confirm`,
        {
          completionData,
        }
      )
      .then(({data}) => this.setBankIdVerifyData(data))
      .catch(() => {
        return this.updateAuthModalName(null);
      });
  }

  // getting data cancelling BankId verification on Contract signing
  getBankIdCancelOnContract() {
    return axiosInstance
      .post(
        `rfp/${this.signContractSideData.side}/contract/${this.signContractSideData.contractId}/signatory/${this.signContractSideData.userId}/cancel`,
        {
          orderRef: this.orderRef,
        }
      )
      .catch(() => this.updateAuthModalName(null));
  }

  // recording BankID data in the store
  setBankIdData(data) {
    this.qrCodeData = data.qrCodeData;
    this.autoStartToken = data.autoStartToken || this.autoStartToken;
    this.orderRef = data.orderRef;
  }

  // recording BankID data after verification in the store
  setBankIdVerifyData(value) {
    this.bankIdVerifyData = value;
    if (this.bankIdVerifyData?.id) {
      this.updateAuthModalName(null);
      showSuccess(i18next.t("VERIFICATION_COMPLETED"));
    }
  }

  // setting new interval for empty insurance data in organization
  async setLastNoInsuranceNotificationDate(payload) {
    try {
      return (
        await axiosInstance.put("user/set-last-no-insurance-notification-date", payload)
      ).data;
    } catch (e) {
      throw new Error(e);
    }
  }

  setUserLastNoInsuranceNotificationDate(userLastNoInsuranceNotificationDate) {
    this.user = {
      ...this.user,
      ActiveOrganization: {
        ...this.user.ActiveOrganization,
        userLastNoInsuranceNotificationDate,
      },
    };
  }

  // getting public rfp filter settings
  async getPublicRequestsMonitoringSetting() {
    return await axiosInstance.get("user/rft-public-monitoring-settings");
  }

  // saving public rfp filter settings
  async setPublicRequestsMonitoringSetting(values) {
    return await axiosInstance.put("user/rft-public-monitoring-settings", values);
  }

  // deleting public rfp filter settings
  async deletePublicRequestsMonitoringSetting() {
    return await axiosInstance.delete("user/rft-public-monitoring-settings");
  }

  // ****** WORKING WITH THIRD-PARTY AUTHORIZATIONS ******
  // getting specific company for monitoring of current user
  getHandyAIToken() {
    return axiosInstance.post("user/handyai-token").then(({data}) => data);
  }
}

const auth = new AuthStore();
export default auth;
